<template>
  <div class="merchandising-configuration-form">
    <vx-card :title="pageTitle" class="merch-form-card">
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <vs-button
            type="border"
            color="danger"
            icon-pack="feather"
            icon="icon-arrow-left"
            @click="handleClose"
          >
            Close
          </vs-button>
        </div>
      </div>

      <div class="vx-row mb-6 form-row">
        <div class="vx-col sm:w-1/4 w-full">
          <label class="vs-input--label">Merchandising ID</label>
        </div>
        <div class="vx-col sm:w-3/4 w-full">
          <vs-input
            class="w-full"
            :value="merchandisingIdDisplay"
            placeholder="Auto Generate"
            :disabled="true"
          />
        </div>
      </div>

      <div class="vx-row mb-6 form-row" v-if="isCreate">
        <div class="vx-col sm:w-1/4 w-full">
          <label class="vs-input--label">Territory</label>
        </div>
        <div class="vx-col sm:w-3/4 w-full">
          <vs-checkbox v-model="allTerritories" class="mb-2">
            All Territories
          </vs-checkbox>
          <multiselect
            class="selectExample"
            v-model="selectedTerritories"
            :options="optionTerritory"
            :multiple="true"
            :allow-empty="true"
            :show-labels="false"
            :max-height="160"
            placeholder="Select MULTI/ALL"
            track-by="id"
            :disabled="allTerritories"
            :internal-search="false"
            :searchable="true"
            @search-change="handlerSearchTerritory"
            :custom-label="customLabelTerritory"
          />
        </div>
      </div>

      <div class="vx-row mb-6 form-row" v-else>
        <div class="vx-col sm:w-1/4 w-full">
          <label class="vs-input--label">Territory</label>
        </div>
        <div class="vx-col sm:w-3/4 w-full">
          <vs-input
            class="w-full"
            :value="territoryDisplay"
            :disabled="true"
          />
        </div>
      </div>

      <div class="items-section mb-6">
        <div class="flex justify-between items-center mb-3">
          <h5 class="mb-0">Checklist Items</h5>
          <vs-button
            v-if="!isView"
            size="small"
            color="primary"
            type="border"
            icon-pack="feather"
            icon="icon-plus"
            @click="addItemRow"
          >
            Add Row
          </vs-button>
        </div>

        <div class="vs-con-table items-table-wrap">
          <table class="vs-table vs-table--tbody-table items-table">
            <thead>
              <tr>
                <th v-if="!isView" class="th-remove"></th>
                <th>Label</th>
                <th class="th-seq">Sequence</th>
                <th>Valid From</th>
                <th>Valid To</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in items" :key="'item-' + index">
                <td v-if="!isView" class="td-remove">
                  <vs-button
                    size="small"
                    color="primary"
                    type="flat"
                    icon-pack="feather"
                    icon="icon-x"
                    @click="removeItemRow(index)"
                  />
                </td>
                <td>
                  <vs-input
                    v-model="row.label"
                    class="w-full"
                    placeholder="Label"
                    :disabled="isView"
                  />
                </td>
                <td>
                  <vs-input
                    v-model.number="row.sequence"
                    type="number"
                    class="w-full sequence-input"
                    :disabled="isView"
                  />
                </td>
                <td>
                  <datepicker
                    :name="'valid_from_' + index"
                    format="yyyy-MM-dd"
                    :inline="false"
                    v-model="row.valid_from"
                    placeholder="Select Date"
                    :disabled="isView"
                  />
                </td>
                <td>
                  <datepicker
                    :name="'valid_to_' + index"
                    format="yyyy-MM-dd"
                    :inline="false"
                    v-model="row.valid_to"
                    placeholder="Select Date"
                    :disabled="isView"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-if="!items.length" class="empty-hint mt-2">
          No items. Click Add Row to add checklist items.
        </p>
      </div>

      <div class="form-footer-actions">
        <vs-button
          v-if="!isView"
          color="primary"
          class="mr-2"
          @click="handleSave"
        >
          SAVE
        </vs-button>
        <vs-button type="border" color="danger" @click="handleClose">
          Close
        </vs-button>
      </div>
    </vx-card>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import moment from "moment";
import {
  BASE_URL,
  TERRITORY_URL,
  isApiSuccess,
  hasAllowedAction,
  formatTerritoryLabel,
  emptyItemRow,
} from "./api";

export default {
  name: "MerchandisingConfigurationForm",
  components: {
    Datepicker,
  },
  data() {
    return {
      recordId: null,
      merchandisingId: "",
      territoryDisplay: "",
      allTerritories: false,
      selectedTerritories: [],
      optionTerritory: [],
      searchTerritory: "",
      items: [emptyItemRow(1)],
    };
  },
  computed: {
    formMode() {
      const name = this.$route.name;
      if (name === "merchandising-configuration-create") return "create";
      if (name === "merchandising-configuration-edit") return "edit";
      if (name === "merchandising-configuration-view") return "view";
      return "view";
    },
    isCreate() {
      return this.formMode === "create";
    },
    isEdit() {
      return this.formMode === "edit";
    },
    isView() {
      return this.formMode === "view";
    },
    pageTitle() {
      if (this.isCreate) return "Create Merchandising Configuration";
      if (this.isEdit) return "Edit Merchandising Configuration";
      return "View Merchandising Configuration";
    },
    merchandisingIdDisplay() {
      if (this.isCreate) return "";
      return this.merchandisingId || "";
    },
  },
  mounted() {
    if (this.isCreate) {
      this.getTerritory();
      return;
    }
    const id = this.$route.params.id;
    if (!id) {
      this.notifyError("Invalid record id");
      this.handleClose();
      return;
    }
    this.recordId = id;
    this.loadRecord();
  },
  methods: {
    customLabelTerritory: formatTerritoryLabel,
    handlerSearchTerritory(query) {
      this.searchTerritory = query;
      this.getTerritory();
    },
    getTerritory() {
      this.$http
        .get(TERRITORY_URL, { params: { search: this.searchTerritory } })
        .then((resp) => {
          if (resp.code === 200 && resp.data && resp.data.records) {
            this.optionTerritory = resp.data.records;
          }
        })
        .catch(() => {});
    },
    loadRecord() {
      this.$vs.loading();
      this.$http
        .get(`${BASE_URL}/${this.recordId}`)
        .then((resp) => {
          this.$vs.loading.close();
          if (!isApiSuccess(resp) || !resp.data) {
            this.notifyError(resp.message || "Failed to load record");
            this.handleClose();
            return;
          }
          const data = resp.data;
          if (this.isEdit && !hasAllowedAction(data, "edit")) {
            this.notifyError("Edit is not allowed for this record");
            this.$router.replace({
              name: "merchandising-configuration-view",
              params: { id: this.recordId },
            });
            return;
          }
          this.bindRecord(data);
        })
        .catch(() => {
          this.$vs.loading.close();
          this.notifyError("Failed to load record");
          this.handleClose();
        });
    },
    bindRecord(data) {
      this.merchandisingId = data.merchandising_id || "";
      if (data.territory) {
        this.territoryDisplay = formatTerritoryLabel(data.territory);
      } else {
        this.territoryDisplay = `${data.territory_code || ""} ${
          data.territory_name || ""
        }`.trim();
      }
      const rows = data.items || [];
      this.items = rows.length
        ? rows.map((item, idx) => ({
            id: item.id,
            label: item.label || "",
            sequence: item.sequence != null ? item.sequence : idx + 1,
            valid_from: item.valid_from
              ? new Date(item.valid_from)
              : null,
            valid_to: item.valid_to ? new Date(item.valid_to) : null,
          }))
        : [emptyItemRow(1)];
    },
    addItemRow() {
      this.items.push(emptyItemRow(this.items.length + 1));
    },
    removeItemRow(index) {
      if (this.items.length <= 1) {
        this.notifyError("At least one item row is required");
        return;
      }
      this.items.splice(index, 1);
    },
    formatDateForApi(value) {
      if (!value) return null;
      return moment(value).format("YYYY-MM-DD");
    },
    mapItemsForPayload() {
      return this.items.map((row, idx) => {
        const item = {
          label: (row.label || "").trim(),
          sequence: row.sequence != null ? row.sequence : idx + 1,
          valid_from: this.formatDateForApi(row.valid_from),
          valid_to: this.formatDateForApi(row.valid_to),
        };
        if (row.id) {
          item.id = row.id;
        }
        return item;
      });
    },
    validateItems() {
      if (!this.items.length) {
        this.notifyError("At least one item is required");
        return false;
      }
      for (let i = 0; i < this.items.length; i++) {
        const row = this.items[i];
        if (!(row.label && row.label.trim())) {
          this.notifyError(`Label is required on row ${i + 1}`);
          return false;
        }
        if (!row.valid_from || !row.valid_to) {
          this.notifyError(`Valid dates are required on row ${i + 1}`);
          return false;
        }
      }
      return true;
    },
    validateCreate() {
      if (!this.allTerritories && !this.selectedTerritories.length) {
        this.notifyError("Select at least one territory or All Territories");
        return false;
      }
      return this.validateItems();
    },
    handleSave() {
      if (this.isCreate) {
        this.saveCreate();
      } else if (this.isEdit) {
        this.saveEdit();
      }
    },
    saveCreate() {
      if (!this.validateCreate()) return;
      const body = {
        all_territories: this.allTerritories,
        territory_ids: this.allTerritories
          ? []
          : this.selectedTerritories.map((t) => t.id),
        items: this.mapItemsForPayload(),
      };
      this.$vs.loading();
      this.$http
        .post(BASE_URL, body)
        .then((resp) => {
          this.$vs.loading.close();
          if (isApiSuccess(resp)) {
            let text = resp.message || "Configuration created successfully";
            if (resp.data && resp.data.created_count != null) {
              text += ` (${resp.data.created_count} created)`;
            }
            if (
              resp.data &&
              resp.data.skipped_territories &&
              resp.data.skipped_territories.length
            ) {
              text += `. Skipped territories: ${resp.data.skipped_territories.join(
                ", "
              )}`;
              this.$vs.notify({
                color: "warning",
                title: "Warning",
                text: `Skipped: ${resp.data.skipped_territories.join(", ")}`,
                position: "top-right",
              });
            }
            this.notifySuccess(text);
            this.$router.push({ name: "merchandising-configuration" });
          } else {
            this.notifyError(resp.message || "Failed to create");
          }
        })
        .catch(() => {
          this.$vs.loading.close();
          this.notifyError("Failed to create");
        });
    },
    saveEdit() {
      if (!this.validateItems()) return;
      this.$vs.loading();
      this.$http
        .put(`${BASE_URL}/${this.recordId}`, { items: this.mapItemsForPayload() })
        .then((resp) => {
          this.$vs.loading.close();
          if (isApiSuccess(resp)) {
            this.notifySuccess(resp.message || "Configuration saved");
            this.$router.push({ name: "merchandising-configuration" });
          } else {
            this.notifyError(resp.message || "Failed to save");
          }
        })
        .catch(() => {
          this.$vs.loading.close();
          this.notifyError("Failed to save");
        });
    },
    handleClose() {
      this.$router.push({ name: "merchandising-configuration" });
    },
    notifySuccess(text) {
      this.$vs.notify({
        color: "success",
        title: "Success",
        text,
        position: "top-right",
      });
    },
    notifyError(text) {
      this.$vs.notify({
        color: "danger",
        title: "Error",
        text,
        position: "top-right",
      });
    },
  },
};
</script>

<style scoped>
.form-row {
  max-width: 720px;
}

.items-table-wrap {
  overflow-x: auto;
}

.items-table {
  width: 100%;
  table-layout: fixed;
}

.items-table thead th {
  font-weight: 600;
  font-size: 12px;
  padding: 10px 8px;
}

.th-remove {
  width: 48px;
}

.th-seq {
  width: 90px;
}

.sequence-input {
  max-width: 80px;
}

.form-footer-actions {
  margin-top: 1.5rem;
}

.empty-hint {
  color: rgba(var(--vs-text), 0.6);
  font-size: 14px;
}
</style>
